import { useTranslation } from 'react-i18next';
import { useEntity } from '../../api/hooks.js';
import { PanelShell } from '../panel-shell.js';
import { ScopeBadge } from '../scope-badge.js';
import { usePanelEntityId, type PanelId, type TabId } from '../store.js';

// Shows the identity of whatever entity this panel is currently following.
// When the panel is LINKED, the entity comes from the tab's link group; when
// PINNED, from the panel's own state. Either way the same hook returns the
// right id.
export function IdentityPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const { t } = useTranslation();
  const entityId = usePanelEntityId(tabId, panelId);
  const { data, isLoading, error } = useEntity(entityId);

  return (
    <PanelShell
      tabId={tabId}
      panelId={panelId}
      loading={!!entityId && isLoading}
      error={entityId && error ? t('panels.identity.error') : false}
      empty={!entityId ? t('panels.identity.noSelection') : false}
    >
      {data && (
        <div style={{ padding: 'var(--space-4)' }}>
          <header
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
            }}
          >
            <div>
              <h3
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 'var(--font-weight-semibold)',
                }}
              >
                {data.data.legalName ?? '—'}
              </h3>
              <div
                className="num"
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--fg-muted)',
                  marginTop: 'var(--space-1)',
                }}
              >
                {data.data.countryCode} · {data.data.registryId}
              </div>
            </div>
            <ScopeBadge scope={data.data.scopeFlag} />
          </header>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: 'var(--space-4)',
              rowGap: 'var(--space-2)',
              margin: 0,
              fontSize: 'var(--font-size-sm)',
            }}
          >
            <Row label={t('panels.identity.fields.legalForm')} value={data.data.legalForm} />
            <Row label={t('panels.identity.fields.status')} value={data.data.status} />
            <Row label={t('panels.identity.fields.primaryNace')} value={data.data.primaryNace} />
            <Row label={t('panels.identity.fields.subSegment')} value={data.data.subSegment} />
            <Row label={t('panels.identity.fields.city')} value={data.data.city} />
            <Row
              label={t('panels.identity.fields.registrationDate')}
              value={data.data.registrationDate?.slice(0, 10) ?? null}
            />
          </dl>
        </div>
      )}
    </PanelShell>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <>
      <dt
        style={{
          color: 'var(--fg-muted)',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0, color: 'var(--fg-default)' }}>{value ?? '—'}</dd>
    </>
  );
}
